"use client";
/*
 * Documentation:
 * Badge — https://app.subframe.com/4f62853ee923/library?component=Badge_97bdb082-1124-4dd7-a335-b14b822d0157
 * DC_MetaIntro — https://app.subframe.com/4f62853ee923/library?component=DC_MetaIntro_75991cf2-d5b4-4e00-98d9-3b0edce8f0fd
 * DC_MetaIntro_ActionStack — https://app.subframe.com/4f62853ee923/library?component=DC_MetaIntro_ActionStack_c2a1f933-7ab4-4ec2-94fe-f4cfdc04d0e0
 * Link Button — https://app.subframe.com/4f62853ee923/library?component=Link+Button_a4ee726a-774c-4091-8c49-55b659356024
 */

import React from "react";
import { FeatherArrowUpRight } from "@subframe/core";
import { FeatherBadgeInfo } from "@subframe/core";
import { FeatherLayers } from "@subframe/core";
import { FeatherUser } from "@subframe/core";
import * as SubframeUtils from "../utils";
import { Badge } from "./Badge";
import { Dc_MetaIntro_ActionStack } from "./Dc_MetaIntro_ActionStack";
import { LinkButton } from "./LinkButton";

interface TranslationLinkProps extends React.ComponentProps<typeof LinkButton> {
  children?: React.ReactNode;
  className?: string;
}

const TranslationLink = React.forwardRef<
  React.ElementRef<typeof LinkButton>,
  TranslationLinkProps
>(function TranslationLink(
  { children, className, ...otherProps }: TranslationLinkProps,
  ref
) {
  return (
    <LinkButton
      className={className}
      variant="brand"
      size="small"
      iconRight={<FeatherArrowUpRight />}
      ref={ref}
      {...otherProps}
    >
      {children}
    </LinkButton>
  );
});

interface VersionLinkProps extends React.ComponentProps<typeof LinkButton> {
  children?: React.ReactNode;
  versionLinkIcon?: React.ReactNode;
  className?: string;
}

const VersionLink = React.forwardRef<
  React.ElementRef<typeof LinkButton>,
  VersionLinkProps
>(function VersionLink(
  {
    children,
    versionLinkIcon = <FeatherLayers />,
    className,
    ...otherProps
  }: VersionLinkProps,
  ref
) {
  return (
    <LinkButton
      className={className}
      size="small"
      icon={versionLinkIcon}
      ref={ref}
      {...otherProps}
    >
      {children}
    </LinkButton>
  );
});

interface MoreInformationLinkProps
  extends React.ComponentProps<typeof LinkButton> {
  children?: React.ReactNode;
  className?: string;
}

const MoreInformationLink = React.forwardRef<
  React.ElementRef<typeof LinkButton>,
  MoreInformationLinkProps
>(function MoreInformationLink(
  { children, className, ...otherProps }: MoreInformationLinkProps,
  ref
) {
  return (
    <LinkButton
      className={className}
      variant="brand"
      size="small"
      icon={<FeatherBadgeInfo />}
      ref={ref}
      {...otherProps}
    >
      {children}
    </LinkButton>
  );
});

interface AuthorBadgeProps extends React.ComponentProps<typeof Badge> {
  children?: React.ReactNode;
  className?: string;
}

const AuthorBadge = React.forwardRef<
  React.ElementRef<typeof Badge>,
  AuthorBadgeProps
>(function AuthorBadge(
  { children, className, ...otherProps }: AuthorBadgeProps,
  ref
) {
  return (
    <Badge
      className={className}
      icon={<FeatherUser />}
      ref={ref}
      {...otherProps}
    >
      {children}
    </Badge>
  );
});

interface TypeBadgeProps extends React.ComponentProps<typeof Badge> {
  children?: React.ReactNode;
  className?: string;
}

const TypeBadge = React.forwardRef<
  React.ElementRef<typeof Badge>,
  TypeBadgeProps
>(function TypeBadge(
  { children, className, ...otherProps }: TypeBadgeProps,
  ref
) {
  return (
    <Badge className={className} variant="neutral" ref={ref} {...otherProps}>
      {children}
    </Badge>
  );
});

interface Dc_MetaIntroRootProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "title"> {
  title?: React.ReactNode;
  paperTypeText?: React.ReactNode;
  publishedText?: React.ReactNode;
  versionLinkSlot?: React.ReactNode;
  translationLinkSlot?: React.ReactNode;
  moreInformationLinkSlot?: React.ReactNode;
  authorsSlot?: React.ReactNode;
  titleSlot?: React.ReactNode;
  typeSlot?: React.ReactNode;
  hideSecondDivider?: boolean;
  isPrint?: boolean;
  actionSlot?: React.ReactNode;
  shareSlot?: React.ReactNode;
  className?: string;
}

const Dc_MetaIntroRoot = React.forwardRef<
  HTMLDivElement,
  Dc_MetaIntroRootProps
>(function Dc_MetaIntroRoot(
  {
    title,
    paperTypeText,
    publishedText,
    versionLinkSlot,
    translationLinkSlot,
    moreInformationLinkSlot,
    authorsSlot,
    titleSlot,
    typeSlot,
    hideSecondDivider = false,
    isPrint = false,
    actionSlot,
    shareSlot,
    className,
    ...otherProps
  }: Dc_MetaIntroRootProps,
  ref
) {
  return (
    <div
      className={SubframeUtils.twClassNames(
        "group/75991cf2 flex w-full flex-col items-start gap-6",
        className
      )}
      ref={ref}
      {...otherProps}
    >
      {typeSlot ? (
        <div className="flex flex-col items-start gap-6">{typeSlot}</div>
      ) : null}
      {titleSlot ? (
        <div className="flex flex-col items-start gap-6">{titleSlot}</div>
      ) : null}
      {authorsSlot ? (
        <div className="flex items-start gap-4">{authorsSlot}</div>
      ) : null}
      <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />
      <div className="flex w-full items-start justify-between">
        <div className="flex items-start gap-2">
          {publishedText ? (
            <span className="text-caption-bold font-caption-bold text-default-font">
              {publishedText}
            </span>
          ) : null}
          {versionLinkSlot ? (
            <div className="flex items-start gap-2">{versionLinkSlot}</div>
          ) : null}
        </div>
        {translationLinkSlot ? (
          <div className="flex items-center max-w-[50%]">
            {translationLinkSlot}
          </div>
        ) : null}
      </div>
      <div
        className={SubframeUtils.twClassNames(
          "flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border",
          { hidden: isPrint }
        )}
      />
      <Dc_MetaIntro_ActionStack
        className={SubframeUtils.twClassNames({ hidden: isPrint })}
        actionSlot={
          actionSlot ? (
            <div className="flex flex-wrap items-start gap-4">{actionSlot}</div>
          ) : null
        }
        shareSlot={
          shareSlot ? (
            <div className="flex items-start justify-between">{shareSlot}</div>
          ) : null
        }
      />
      <div
        className={SubframeUtils.twClassNames(
          "flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border",
          { hidden: isPrint || hideSecondDivider }
        )}
      />
      {moreInformationLinkSlot ? (
        <div
          className={SubframeUtils.twClassNames(
            "flex flex-col items-start gap-6",
            { hidden: isPrint }
          )}
        >
          {moreInformationLinkSlot}
        </div>
      ) : null}
    </div>
  );
});

export const Dc_MetaIntro = Object.assign(Dc_MetaIntroRoot, {
  TranslationLink,
  VersionLink,
  MoreInformationLink,
  AuthorBadge,
  TypeBadge,
});
